import { useState } from 'react';
import { useApi } from '../useApi';
import { ErrorNotice } from '../components/ErrorNotice';

// Run the retention sweep on demand and read back what it did.
//
// This exists so that arming the thing is not the first time anyone sees what
// it intends to delete. The sweep runs weekly on a timer; this button runs the
// SAME function with the SAME mode. It is deliberately not a "force" switch —
// if it could delete while the timer was still reporting, the rehearsal would
// be testing something other than what ships.

interface SweepResult {
  mode: 'report' | 'delete';
  candidates: number;
  deleted: number;
  note: string;
}

export function RetentionPage() {
  const api = useApi();
  const [result, setResult] = useState<SweepResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (!api) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.post<SweepResult>('/support/retention/run', {}));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The sweep could not be run.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <ErrorNotice message={error} />

      <div className="card">
        <h2>Data retention sweep</h2>
        <p className="muted">
          Customers are told their data is permanently deleted 30 days after their access
          ends. This is the job that keeps that promise. It runs every Sunday; this button
          runs the same job now, in whatever mode the server is set to.
        </p>
        <button type="button" onClick={() => void run()} disabled={running || !api}>
          {running ? 'Running…' : 'Run the sweep now'}
        </button>
      </div>

      {result && (
        <div className={`notice ${result.mode === 'delete' ? 'warn' : 'success'}`}>
          <strong>
            {result.mode === 'delete' ? 'Armed — this deleted data.' : 'Report only.'}
          </strong>{' '}
          {result.candidates} tenant{result.candidates === 1 ? '' : 's'} past the retention
          window; {result.deleted} deleted.
          <br />
          {result.note}
          <br />
          <span className="muted small">
            Every candidate is written to the Audit log, whether or not it was deleted —
            read it there to see exactly which records were named.
          </span>
        </div>
      )}
    </>
  );
}
